import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Activity,
  AlertCircle,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRightLeft,
  Banknote,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Edit,
  FileText,
  HandCoins,
  History,
  Loader2,
  Mail,
  MessageSquare,
  Minus,
  Phone,
  Plus,
  Save,
  Search,
  TrendingUp,
  User,
  UserPlus,
  X,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { LogCallModal } from '@/components/collections/action-modals';
import { PromiseDetailsSidebar } from '@/components/collections/promise-details-sidebar';
import { PromiseSendEmailModal } from '@/components/collections/promise-send-email-modal';
import { SelectTransactionsModal } from '@/components/collections/select-transactions-modal';
import { HelpSupportButton } from '@/components/help-support/help-support-button';
import { AddContactDialog } from '@/components/shared/add-contact-dialog';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { CurrencyIcon } from '@/components/shared/currency-icon';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { TransactionTable } from '@/components/transactions/transaction-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { useCompany } from '@/contexts/CompanyContext';
import { useAddPromiseNote } from '@/hooks/useAddPromiseNote';
import { useCustomerContactsList } from '@/hooks/useCustomerContacts';
import { useExportAll } from '@/hooks/useExportAll';
import { usePromiseAuditLogsGraphQL } from '@/hooks/usePromiseAuditLogsGraphQL';
import type { PromiseUnifiedActivity } from '@/hooks/usePromiseRecentActivities';
import { usePromiseRecentActivitiesGraphQL } from '@/hooks/usePromiseRecentActivitiesGraphQL';
import {
  mapConfirmationMethodToId,
  type UpdatePromiseToPayRequest,
  usePromiseById,
  useUpdatePromiseToPay,
} from '@/hooks/usePromisesToPayData';
import {
  buildPromiseTransactionExportFilter,
  PROMISE_TRANSACTION_EXPORT_COLUMN_MAP,
  type PromiseTransaction,
  usePromiseTransactionsPaginatedGraphQL,
} from '@/hooks/usePromiseTransactionsGraphQL';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { cn, formatDate, formatDateDDMMYYYY, formatDateTime } from '@/lib/utils';
import { formatCurrency, roundCurrency } from '@/lib/utils/formatters';
import { ExportProvider } from '@/providers/ExportProvider';
import type { PromiseAuditLog, PromiseToPay } from '@/types/collections';
import type { ColumnInput } from '@/types/export';
import { Forms } from '@/types/export';
import type { ColumnVisibility, Transaction } from '@/types/transaction';

interface EditablePromiseData {
  promiseDate: Date;

  promisedAmount: number;

  status: PromiseToPay['status'];

  confirmationMethod: PromiseToPay['confirmationMethod'];

  assignedUserId: number;

  contactId: string;

  notes: string;
}

interface PromiseValidationError {
  message: string;
}

function validatePromiseChanges(
  editedData: EditablePromiseData,

  promise: PromiseToPay,

  calculatedPromisedAmount: number,

  promisedInvoiceIds: Set<string>,

  promisedAmounts: Map<string, number>,

  outstandingByTxId: Map<string, number>,
): PromiseValidationError | null {
  if (promisedInvoiceIds.size === 0) {
    return { message: 'At least one transaction must be selected' };
  }

  if (calculatedPromisedAmount <= 0) {
    return { message: 'The total promise amount must be greater than zero' };
  }

  if (!editedData.promiseDate) {
    return { message: 'Promise date is required' };
  }

  for (const invoiceId of Array.from(promisedInvoiceIds)) {
    const invoice = promise.invoices.find((inv) => inv.id === invoiceId || String(inv.transactionId) === invoiceId);

    const allocated = promisedAmounts.get(invoiceId) ?? 0;

    // Use the display outstanding amount (currency-adjusted) as the cap
    // For pending transactions not yet in promise.invoices, fall back to outstandingByTxId
    const outstanding =
      outstandingByTxId.get(invoiceId) ?? invoice?.outstandingAmount ?? invoice?.balanceRemaining ?? 0;

    if (!invoice && !outstandingByTxId.has(invoiceId)) continue;

    const reference = invoice?.headerReference ?? invoiceId;

    if (outstanding !== 0 && allocated !== 0 && Math.sign(allocated) !== Math.sign(outstanding)) {
      return {
        message: `Allocation for ${reference} has incorrect sign \u2014 must match the outstanding amount sign`,
      };
    }

    if (roundCurrency(Math.abs(allocated)) > roundCurrency(Math.abs(outstanding))) {
      return {
        message: `Allocation for ${reference} exceeds outstanding amount of ${formatCurrency(outstanding)}`,
      };
    }
  }

  const totalOutstandingError = validateTotalOutstanding(
    calculatedPromisedAmount,
    promise,
    promisedInvoiceIds,
    outstandingByTxId,
  );
  return totalOutstandingError;
}

function validateTotalOutstanding(
  calculatedPromisedAmount: number,
  promise: PromiseToPay,
  promisedInvoiceIds: Set<string>,
  outstandingByTxId: Map<string, number>,
): PromiseValidationError | null {
  let totalOutstanding = 0;
  for (const invoiceId of Array.from(promisedInvoiceIds)) {
    const invoice = promise.invoices.find((inv) => inv.id === invoiceId || String(inv.transactionId) === invoiceId);
    const outstanding = outstandingByTxId.get(invoiceId) ?? invoice?.outstandingAmount ?? invoice?.balanceRemaining;
    if (outstanding === undefined) continue;
    totalOutstanding += outstanding;
  }

  if (roundCurrency(Math.abs(calculatedPromisedAmount)) > roundCurrency(Math.abs(totalOutstanding))) {
    return {
      message: `Total promised amount (${formatCurrency(calculatedPromisedAmount)}) exceeds total outstanding (${formatCurrency(totalOutstanding)})`,
    };
  }

  return null;
}

function validatePromisedAmount(
  numValue: number,

  outstanding: number,
): string | null {
  if (outstanding !== 0 && numValue !== 0 && Math.sign(numValue) !== Math.sign(outstanding)) {
    return `Amount sign must match the outstanding amount sign (${outstanding >= 0 ? 'positive' : 'negative'})`;
  }

  if (roundCurrency(Math.abs(numValue)) > roundCurrency(Math.abs(outstanding))) {
    return `Amount cannot exceed outstanding amount of ${formatCurrency(outstanding)}`;
  }

  return null;
}

function getStatusBadge(status: PromiseToPay['status']) {
  const variants: Record<PromiseToPay['status'], { className: string; icon: React.ReactNode }> = {
    Active: {
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',

      icon: <CheckCircle2 className="h-3 w-3" />,
    },

    Broken: {
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',

      icon: <AlertCircle className="h-3 w-3" />,
    },

    Paid: {
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',

      icon: <CheckCircle2 className="h-3 w-3" />,
    },

    'Partially Paid': {
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',

      icon: <Clock className="h-3 w-3" />,
    },

    Closed: {
      className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',

      icon: <X className="h-3 w-3" />,
    },
  };

  return (
    <Badge className={`${variants[status].className} flex items-center gap-1 w-fit`}>
      {variants[status].icon}

      {status}
    </Badge>
  );
}

function getConfirmationMethodBadge(method: PromiseToPay['confirmationMethod']) {
  const icons = {
    Phone: <Phone className="h-3 w-3" />,

    Email: <Mail className="h-3 w-3" />,

    Portal: <TrendingUp className="h-3 w-3" />,
  };

  return (
    <Badge variant="outline" className="flex items-center gap-1 w-fit">
      {icons[method]}

      {method}
    </Badge>
  );
}

function getActivityIcon(type: PromiseUnifiedActivity['type']) {
  const icons: Record<PromiseUnifiedActivity['type'], React.ReactNode> = {
    email: <Mail className="h-4 w-4 text-blue-600" />,

    call: <Phone className="h-4 w-4 text-green-600" />,

    sms: <MessageSquare className="h-4 w-4 text-green-600" />,

    note: <FileText className="h-4 w-4 text-purple-600" />,

    promise: <HandCoins className="h-4 w-4 text-amber-600" />,

    payment: <Banknote className="h-4 w-4 text-emerald-600" />,

    status_change: <Activity className="h-4 w-4 text-amber-600" />,

    system: <Activity className="h-4 w-4 text-gray-600" />,

    case_note: <FileText className="h-4 w-4 text-blue-600" />,

    dispute: <AlertCircle className="h-4 w-4 text-red-600" />,

    communication: <MessageSquare className="h-4 w-4 text-green-600" />,

    case: <FileText className="h-4 w-4 text-blue-600" />,
  };

  return icons[type];
}

function getAuditLogIcon(action: PromiseAuditLog['action']) {
  switch (action) {
    case 'status_change':
      return ArrowRightLeft;

    case 'amount_change':
      return Banknote;

    case 'date_change':
      return Calendar;

    case 'collection_reassignment':
      return UserPlus;

    case 'field_update':
      return Edit;

    case 'note_added':
      return FileText;

    case 'promise_created':
      return History;

    case 'assigned_user_updated':
      return UserPlus;

    case 'transaction_linked':
      return Plus;

    case 'transaction_unlinked':
      return Minus;

    case 'phone_call_logged':
      return Phone;

    default:
      return Activity;
  }
}

function getAuditLogColor(action: PromiseAuditLog['action']) {
  switch (action) {
    case 'status_change':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';

    case 'amount_change':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';

    case 'date_change':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';

    case 'collection_reassignment':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';

    case 'field_update':
      return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';

    case 'note_added':
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';

    case 'promise_created':
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';

    case 'assigned_user_updated':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';

    case 'transaction_linked':
      return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';

    case 'transaction_unlinked':
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';

    case 'phone_call_logged':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';

    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  }
}

function getTagColor(tag: PromiseUnifiedActivity['tag']) {
  switch (tag) {
    case 'Activity':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';

    case 'Note':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800';

    case 'Communication':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';

    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
  }
}

function getIconBackgroundColor(tag: PromiseUnifiedActivity['tag']) {
  switch (tag) {
    case 'Activity':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';

    case 'Note':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';

    case 'Communication':
      return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';

    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  }
}

const DEFAULT_PROMISE_DETAIL_TX_COLUMNS: ColumnDefinition[] = [
  {
    id: 'referenceNumber',
    label: 'Reference Number',
    visible: true,
    order: 0,
    locked: true,
  },

  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },

  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },

  { id: 'transactionType', label: 'Type', visible: true, order: 3 },

  { id: 'amount', label: 'Gross', visible: true, order: 4 },

  { id: 'currency', label: 'Currency', visible: true, order: 5 },

  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 6 },

  {
    id: 'promisedAmount',
    label: 'Promised Amount',
    visible: true,
    order: 7,
    locked: true,
  },

  { id: 'status', label: 'Status', visible: true, order: 8 },

  { id: 'description', label: 'Description', visible: false, order: 9 },

  { id: 'accountCode', label: 'Account', visible: false, order: 10 },

  { id: 'netAmount', label: 'Net', visible: false, order: 11 },

  { id: 'vatAmount', label: 'Tax', visible: false, order: 12 },

  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 13 },

  { id: 'orderNumber', label: 'Order Number', visible: false, order: 14 },

  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },

  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 16 },

  { id: 'customerName', label: 'Customer Name', visible: false, order: 17 },

  { id: 'flags', label: 'Flags', visible: true, order: 18 },

  { id: 'onDispute', label: 'External Dispute', visible: false, order: 19 },

  { id: 'actions', label: 'Actions', visible: true, order: 20 },
];

interface OverviewTabContentProps {
  promise: PromiseToPay;
  isEditMode: boolean;
  editedData: EditablePromiseData | null;
  displayData: EditablePromiseData | PromiseToPay;
  navigate: ReturnType<typeof useNavigate>;
  setSendEmailModalOpen: (open: boolean) => void;
  setLogCallModalOpen: (open: boolean) => void;
  setActiveTab: (tab: string) => void;
  setEditedData: (data: EditablePromiseData | null) => void;
  columnVisibility: ColumnVisibility;
  promiseTransactions: PromiseTransaction[];
  promisedInvoiceIds: Set<string>;
  promisedAmounts: Map<string, number>;
  calculatedPromisedAmount: number;
  handlePromisedAmountChange: (invoiceId: string, value: number | null) => void;
  handleTransactionClick: (transaction: Transaction) => void;
  setPromisedInvoiceIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleExportCurrentView: () => void;
  handleExportAllColumns: () => void;
  handleColumnConfigApply: (columns: ColumnDefinition[]) => void;
  resetPromiseDetailPrefs: () => void;
  promiseDetailCols: ColumnDefinition[];
  activeUsers: Array<{
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    status: string;
  }>;
  txPagination: {
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    page: number;
    pageSize: number;
  };
  txCurrentPage: number;
  txItemsPerPage: number;
  setTxCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setTxItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  transactionSearchQuery: string;
  setTransactionSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  onOpenAddTransactionModal: () => void;
  pendingTransactions: Transaction[];
  allPendingTransactionIds: Set<number>;
  visibleTransactions: Transaction[];
  totalOutstandingAll: number;
  filteredPendingCount: number;
  contacts: Array<{ id: string; firstName: string; surname: string; isPrimary: boolean }>;
  setAddContactDialogOpen: (open: boolean) => void;
}

const OverviewTabContent: React.FC<OverviewTabContentProps> = ({
  promise,
  isEditMode,
  editedData,
  displayData,
  navigate,
  setSendEmailModalOpen,
  setLogCallModalOpen,
  setActiveTab,
  setEditedData,
  columnVisibility,
  promiseTransactions,
  promisedInvoiceIds,
  promisedAmounts,
  calculatedPromisedAmount,
  handlePromisedAmountChange,
  handleTransactionClick,
  setPromisedInvoiceIds,
  handleExportCurrentView,
  handleExportAllColumns,
  handleColumnConfigApply,
  resetPromiseDetailPrefs,
  promiseDetailCols,
  activeUsers,
  txPagination,
  txCurrentPage,
  txItemsPerPage,
  setTxCurrentPage,
  setTxItemsPerPage,
  onSortChange,
  transactionSearchQuery,
  setTransactionSearchQuery,
  onOpenAddTransactionModal,
  pendingTransactions,
  allPendingTransactionIds,
  visibleTransactions,
  totalOutstandingAll,
  filteredPendingCount,
  contacts,
  setAddContactDialogOpen,
}) => {
  const { selectedCompany } = useCompany();
  return (
    <TabsContent value="overview" className="px-6 py-4 space-y-6 min-w-0 max-w-full">
      {/* Two-Column Layout: Customer Information (Left) and Promise Summary (Right) */}
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        {/* Customer Information Panel - LEFT */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Customer Information
            </CardTitle>
            <CardDescription>Contact details and account information</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Two-column grid layout */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {/* Customer - Left column (combines Account Number and Customer Name) */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Customer</p>
                <p className="text-sm font-semibold">
                  [{promise.customerAccountNumber}] {promise.customerName}
                </p>
              </div>

              {/* Contact Name - Right column */}
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  Contact Name
                  {isEditMode && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 ml-1 p-0"
                      onClick={() => setAddContactDialogOpen(true)}
                      title="Add new contact"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  )}
                </p>
                {isEditMode && editedData ? (
                  <Select
                    value={editedData.contactId || 'none'}
                    onValueChange={(value) => {
                      const contactId = value === 'none' ? '' : value;
                      setEditedData({ ...editedData, contactId });
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select a contact" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {`${contact.firstName} ${contact.surname}`}
                          {contact.isPrimary && ' (Primary)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm font-semibold">{promise.contactName || '-'}</p>
                )}
              </div>

              {/* Email - Left column */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  Email Address
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 w-7 p-0 bg-teal-600 hover:bg-teal-700 text-white"
                    onClick={() => setSendEmailModalOpen(true)}
                    title="Send Email"
                  >
                    <Mail className="h-3.5 w-3.5" />
                  </Button>
                  <a
                    href={`mailto:${promise.customerEmail}`}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline block truncate"
                    title={promise.customerEmail}
                  >
                    {promise.customerEmail}
                  </a>
                </div>
              </div>

              {/* Phone - Right column */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  Phone Number
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    className="h-7 w-7 p-0 bg-teal-600 hover:bg-teal-700 text-white"
                    onClick={() => setLogCallModalOpen(true)}
                    title="Log Call"
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </Button>
                  <a
                    href={`tel:${promise.customerPhone}`}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {promise.customerPhone}
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Promise Summary Panel - RIGHT */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              Promise Summary
            </CardTitle>
            <CardDescription>Key details about this payment commitment</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Three-column grid layout for first row */}
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-x-4">
                {/* Promise Date - First column */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Promise Date
                  </Label>
                  {isEditMode && editedData ? (
                    <Input
                      type="date"
                      value={format(editedData.promiseDate, 'yyyy-MM-dd')}
                      onChange={(e) =>
                        setEditedData({
                          ...editedData,
                          promiseDate: new Date(e.target.value),
                        })
                      }
                      className="h-8"
                    />
                  ) : (
                    <p className="text-sm font-semibold">{formatDate(displayData.promiseDate)}</p>
                  )}
                </div>

                {/* Promised Amount - Second column */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <CurrencyIcon
                      currencyCode={promise.currencyCode || selectedCompany?.currency}
                      className="h-3 w-3"
                    />
                    Promised Amount
                  </Label>
                  <p className="text-sm font-bold">
                    {formatCurrency(
                      calculatedPromisedAmount,
                      promise.currencyCode || selectedCompany?.currency || 'GBP',
                    )}
                  </p>
                </div>

                {/* Assigned User - Third column */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <UserPlus className="h-3 w-3" />
                    Assigned User
                  </Label>
                  {isEditMode && editedData ? (
                    <Select
                      value={String(editedData.assignedUserId)}
                      onValueChange={(value) =>
                        setEditedData({
                          ...editedData,
                          assignedUserId: parseInt(value, 10),
                        })
                      }
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Select user..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activeUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                <AvatarFallback className="text-[10px]">
                                  {user.firstName[0]}
                                  {user.lastName[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span>
                                {user.firstName} {user.lastName}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm font-semibold">{promise.assignedUserName || 'Unassigned'}</p>
                  )}
                </div>
              </div>

              {/* Three-column grid layout for remaining fields */}
              <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                {/* Status - First column */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Activity className="h-3 w-3" />
                    Status
                  </Label>
                  {isEditMode && editedData ? (
                    <Select
                      value={editedData.status}
                      onValueChange={(value) =>
                        setEditedData({
                          ...editedData,
                          status: value as PromiseToPay['status'],
                        })
                      }
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Broken">Broken</SelectItem>
                        <SelectItem value="Paid">Paid</SelectItem>
                        <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div>{getStatusBadge(displayData.status)}</div>
                  )}
                </div>

                {/* Confirmation Method - Second column (aligned under Promised Amount) */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Confirmation Method
                  </Label>
                  {isEditMode && editedData ? (
                    <Select
                      value={editedData.confirmationMethod}
                      onValueChange={(value) =>
                        setEditedData({
                          ...editedData,
                          confirmationMethod: value as PromiseToPay['confirmationMethod'],
                        })
                      }
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Phone">Phone</SelectItem>
                        <SelectItem value="Email">Email</SelectItem>
                        <SelectItem value="Portal">Portal</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div>{getConfirmationMethodBadge(displayData.confirmationMethod)}</div>
                  )}
                </div>

                {/* Case Number - Third column */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    Case Number
                  </Label>
                  {promise.caseNumber ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      onClick={() =>
                        navigate(`/collections/queue/case/${promise.caseId}`, { state: { from: 'promise-details' } })
                      }
                    >
                      {promise.caseNumber}
                    </Button>
                  ) : (
                    <p className="text-sm font-semibold">-</p>
                  )}
                </div>

                {/* Created By - First column */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Created By
                  </p>
                  <p className="text-sm font-semibold">{promise.createdBy}</p>
                </div>

                {/* Created At - Second column (aligned under Promised Amount) */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Created At
                  </p>
                  <p className="text-sm font-semibold">{formatDateTime(promise.createdAt.toISOString())}</p>
                </div>

                {/* Empty third column for alignment */}
                <div></div>

                {/* Days Overdue - Spans 3 columns if present */}
                {promise.daysOverdue && promise.daysOverdue > 0 && (
                  <div className="col-span-3 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Days Overdue
                    </p>
                    <Badge variant="destructive" className="text-sm px-2 py-0.5 w-fit">
                      {promise.daysOverdue} days
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promise Notes Panel - Full Width */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0 space-y-1">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                Promise Notes
              </CardTitle>
              <CardDescription>
                {isEditMode
                  ? 'Edit promise notes or view full history in the Activity tab'
                  : 'Quick view of promise notes'}
              </CardDescription>
            </div>
            <div className="flex-1">
              {isEditMode && editedData ? (
                <Textarea
                  value={editedData.notes}
                  onChange={(e) => setEditedData({ ...editedData, notes: e.target.value })}
                  placeholder="Add notes about this promise..."
                  rows={3}
                  className="resize-none"
                />
              ) : (
                <div className="space-y-2">
                  <p className="text-sm leading-relaxed">{displayData.notes || 'No notes available'}</p>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('activity')} className="gap-2">
                    <History className="h-4 w-4" />
                    View Full Activity History
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Transactions */}
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-green-600 dark:text-green-400" />
              <CardTitle className="text-lg">Transactions</CardTitle>
              <Badge variant="secondary" className="ml-2">
                {txPagination.totalItems + filteredPendingCount}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode && (
                <Button variant="default" size="sm" onClick={onOpenAddTransactionModal} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Transaction
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Quick search..."
                  className="pl-8 h-9 w-[200px]"
                  value={transactionSearchQuery}
                  onChange={(e) => setTransactionSearchQuery(e.target.value)}
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9" title="Export to Excel">
                    <ArrowDownToLine className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportCurrentView}>Export Current View</DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>Export All Columns</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ColumnConfigDialog
                columns={promiseDetailCols}
                onApply={handleColumnConfigApply}
                onReset={resetPromiseDetailPrefs}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isEditMode && pendingTransactions.length > 0 && (
            <div className="mb-3 flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800">
              <Plus className="h-4 w-4" />
              <span className="font-medium">
                {pendingTransactions.length} pending transaction
                {pendingTransactions.length === 1 ? '' : 's'}
              </span>
              <span className="text-muted-foreground">— will be saved when you click Save Changes</span>
            </div>
          )}
          <TransactionTable
            transactions={visibleTransactions}
            columnVisibility={columnVisibility}
            orderedColumns={promiseDetailCols}
            onViewTransaction={handleTransactionClick}
            showCustomerColumn={false}
            displayAmounts={true}
            enableSelection={isEditMode}
            selectedRows={new Set(Array.from(promisedInvoiceIds).map((id) => Number(id)))}
            onSelectRow={(id, checked) => {
              setPromisedInvoiceIds((prev) => {
                const newSet = new Set(prev);
                if (checked) {
                  newSet.add(String(id));
                } else {
                  newSet.delete(String(id));
                }
                return newSet;
              });
            }}
            onSelectAll={(checked) => {
              const allTxIds = [
                ...promiseTransactions.map((tx) => String(tx.id)),
                ...pendingTransactions.map((tx) => String(tx.id)),
              ];
              if (checked) {
                setPromisedInvoiceIds(new Set(allTxIds));
              } else {
                setPromisedInvoiceIds(new Set());
              }
            }}
            pendingTransactionIds={allPendingTransactionIds}
            currentPage={txCurrentPage}
            pageSize={txItemsPerPage}
            totalItems={txPagination.totalItems + filteredPendingCount}
            onPageChange={setTxCurrentPage}
            onPageSizeChange={(size) => {
              setTxItemsPerPage(size);
              setTxCurrentPage(1);
            }}
            onSortChange={onSortChange}
            renderActions={(transaction) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTransactionClick(transaction);
                }}
              >
                View
              </Button>
            )}
            renderPromisedAmount={(transaction) => {
              const tx = transaction as unknown as PromiseTransaction;
              const txId = String(tx.id);
              if (isEditMode) {
                const outstandingAmount = tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0;
                const isNegativeOutstanding = outstandingAmount < 0;
                return (
                  <CurrencyInput
                    currencyCode={tx.currency || selectedCompany?.currency || 'GBP'}
                    value={promisedAmounts.get(txId) ?? tx.promisedAmount ?? 0}
                    onChange={(val) => handlePromisedAmountChange(txId, val)}
                    min={isNegativeOutstanding ? outstandingAmount : 0}
                    max={isNegativeOutstanding ? 0 : outstandingAmount || undefined}
                    sign={isNegativeOutstanding ? 'negative' : 'positive'}
                    className="text-right h-8 w-32"
                  />
                );
              }
              return (
                <span className="font-medium">
                  {formatCurrency(
                    tx.displayPromisedAmount ?? tx.promisedAmount ?? 0,
                    tx.currency || selectedCompany?.currency || 'GBP',
                  )}
                </span>
              );
            }}
          />

          <Separator className="my-4" />
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total Promised Amount:</span>
              <span className="text-xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(
                  Array.from(promisedInvoiceIds).reduce((sum, invoiceId) => {
                    return sum + (promisedAmounts.get(invoiceId) ?? 0);
                  }, 0),
                  promise.currencyCode || selectedCompany?.currency || 'GBP',
                )}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-semibold">Total Outstanding:</span>
              <span className="text-xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(totalOutstandingAll, promise.currencyCode || selectedCompany?.currency || 'GBP')}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

interface ActivityTabContentProps {
  filteredActivities: PromiseUnifiedActivity[];
  activitiesLoading: boolean;
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  newNote: string;
  setNewNote: (note: string) => void;
  handleAddNote: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
}

const ActivityTabContent: React.FC<ActivityTabContentProps> = ({
  filteredActivities,
  activitiesLoading,
  selectedFilters,
  setSelectedFilters,
  clearFilters,
  activeFilterCount,
  newNote,
  setNewNote,
  handleAddNote,
  hasMore,
  isFetchingMore,
  onLoadMore,
}) => {
  return (
    <TabsContent value="activity" className="px-6 py-4 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="text-lg">Activity Timeline</CardTitle>
              <CardDescription>Complete history of all activities and notes</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {filteredActivities.length} {filteredActivities.length === 1 ? 'item' : 'items'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Controls */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Filter by Type
              </Label>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-xs">
                  <X className="h-3 w-3 mr-1" />
                  Clear Filters ({activeFilterCount})
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={selectedFilters.includes('Activity') ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (selectedFilters.includes('Activity')) {
                    setSelectedFilters(selectedFilters.filter((f) => f !== 'Activity'));
                  } else {
                    setSelectedFilters([...selectedFilters, 'Activity']);
                  }
                }}
                className={cn(
                  'h-8',
                  selectedFilters.includes('Activity') &&
                    'bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700',
                )}
              >
                <Activity className="h-4 w-4 mr-2" />
                Activity
              </Button>
              <Button
                variant={selectedFilters.includes('Note') ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (selectedFilters.includes('Note')) {
                    setSelectedFilters(selectedFilters.filter((f) => f !== 'Note'));
                  } else {
                    setSelectedFilters([...selectedFilters, 'Note']);
                  }
                }}
                className={cn(
                  'h-8',
                  selectedFilters.includes('Note') &&
                    'bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-700',
                )}
              >
                <FileText className="h-4 w-4 mr-2" />
                Notes
              </Button>
              <Button
                variant={selectedFilters.includes('Communication') ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (selectedFilters.includes('Communication')) {
                    setSelectedFilters(selectedFilters.filter((f) => f !== 'Communication'));
                  } else {
                    setSelectedFilters([...selectedFilters, 'Communication']);
                  }
                }}
                className={cn(
                  'h-8',
                  selectedFilters.includes('Communication') &&
                    'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700',
                )}
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Communications
              </Button>
            </div>
          </div>

          {/* Add New Note Section */}
          <Separator />
          <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
            <Label htmlFor="newNote" className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Add New Note
            </Label>
            <Textarea
              id="newNote"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
              className="resize-none"
            />
            <div className="flex justify-end">
              <Button onClick={handleAddNote} disabled={!newNote.trim()} size="sm">
                <Save className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </div>
          </div>

          {/* Timeline Display */}
          {activitiesLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {!activitiesLoading && filteredActivities.length > 0 && (
            <>
              <Separator />
              <div className="space-y-4">
                {filteredActivities.map((activity, index) => (
                  <div key={activity.id} className="relative">
                    {index !== filteredActivities.length - 1 && (
                      <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                    )}
                    <div className="flex gap-4">
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                          getIconBackgroundColor(activity.tag),
                        )}
                      >
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className={cn('text-xs font-medium', getTagColor(activity.tag))}>
                                {activity.tag}
                              </Badge>
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">{activity.description}</p>
                            {activity.outcome && (
                              <p className="text-sm text-muted-foreground italic">Outcome: {activity.outcome}</p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(activity.createdAt.toISOString())}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <User className="h-3 w-3 mr-1" />
                            {activity.createdBy}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && onLoadMore && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onLoadMore}
                    disabled={isFetchingMore}
                    className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                  >
                    {isFetchingMore ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
          {!activitiesLoading && filteredActivities.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">No activities match your filters</p>
              <p className="text-xs mt-1">Try adjusting your filter selection</p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                  Clear All Filters
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};

interface AuditLogTabContentProps {
  auditLogs: PromiseAuditLog[];
  auditLogsLoading: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  currencyCode?: string;
}

const AuditLogTabContent: React.FC<AuditLogTabContentProps> = ({
  auditLogs,
  auditLogsLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
  currencyCode = 'GBP',
}) => {
  return (
    <TabsContent value="audit" className="px-6 py-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Audit Log
          </CardTitle>
          <CardDescription>Complete chronological history of all promise modifications and changes</CardDescription>
        </CardHeader>
        <CardContent>
          {auditLogsLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
          {!auditLogsLoading && auditLogs.length > 0 && (
            <div className="space-y-4">
              {auditLogs.map((log, index) => {
                const Icon = getAuditLogIcon(log.action);
                const colorClass = getAuditLogColor(log.action);

                return (
                  <div key={log.id} className="relative">
                    {index !== auditLogs.length - 1 && (
                      <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                    )}
                    <div className="flex gap-4">
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                          colorClass,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-1">
                            <p className="font-medium text-sm">{log.description}</p>

                            {/* Status/Amount/Date/Field Changes */}
                            {(log.action === 'status_change' ||
                              log.action === 'amount_change' ||
                              log.action === 'date_change' ||
                              log.action === 'field_update' ||
                              log.action === 'assigned_user_updated') &&
                              log.oldValue &&
                              log.newValue && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline" className="text-xs">
                                    {log.action === 'amount_change' && typeof log.oldValue === 'number'
                                      ? formatCurrency(log.oldValue)
                                      : log.oldValue}
                                  </Badge>
                                  <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                  <Badge variant="outline" className="text-xs">
                                    {log.action === 'amount_change' && typeof log.newValue === 'number'
                                      ? formatCurrency(log.newValue)
                                      : log.newValue}
                                  </Badge>
                                </div>
                              )}

                            {/* Transaction Linked/Unlinked */}
                            {(log.action === 'transaction_linked' || log.action === 'transaction_unlinked') && (
                              <div className="text-sm text-muted-foreground space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs font-mono">
                                    {log.transactionNumber ||
                                      (log.action === 'transaction_linked' ? log.newValue : log.oldValue) ||
                                      (log.transactionId ? `TXN-${log.transactionId}` : 'Unknown')}
                                  </Badge>
                                  {log.transactionAmount != null && (
                                    <>
                                      <span>•</span>
                                      <span
                                        className={cn(
                                          'font-medium',
                                          log.action === 'transaction_linked'
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-red-600 dark:text-red-400',
                                        )}
                                      >
                                        {formatCurrency(log.transactionAmount, currencyCode)}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {log.transactionDate && (
                                  <div className="text-xs">
                                    Invoice Date: {formatDate(log.transactionDate.toISOString())}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Collection Reassignment */}
                            {log.action === 'collection_reassignment' && log.oldCaseNumber && log.newCaseNumber && (
                              <div className="text-sm text-muted-foreground space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs">From:</span>
                                  <Badge variant="outline" className="text-xs font-mono">
                                    {log.oldCaseNumber}
                                  </Badge>
                                  <ArrowRightLeft className="h-3 w-3" />
                                  <span className="text-xs">To:</span>
                                  <Badge variant="outline" className="text-xs font-mono">
                                    {log.newCaseNumber}
                                  </Badge>
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(log.createdAt.toISOString())}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <User className="h-3 w-3 mr-1" />
                            {log.createdBy}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!auditLogsLoading && auditLogs.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No audit log entries found for this promise.</p>
            </div>
          )}

          {hasMore && onLoadMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onLoadMore}
                disabled={isFetchingMore}
                className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
              >
                {isFetchingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};

/** Client-side search filter for pending transactions — mirrors the server-side summary filter */
function matchesTransactionSearch(tx: Transaction, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const ref = (tx.referenceNumber ?? '').toLowerCase();
  const desc = (tx.description ?? '').toLowerCase();
  const amount = String(tx.amount ?? '');
  const outstanding = String(tx.outstandingAmount ?? '');
  const txDate = tx.transactionDate ? formatDateDDMMYYYY(tx.transactionDate).toLowerCase() : '';
  const dueDate = tx.dueDate ? formatDateDDMMYYYY(tx.dueDate).toLowerCase() : '';
  const customerName = (tx.customerName ?? '').toLowerCase();
  return (
    ref.includes(q) ||
    desc.includes(q) ||
    amount.includes(q) ||
    outstanding.includes(q) ||
    txDate.includes(q) ||
    dueDate.includes(q) ||
    customerName.includes(q)
  );
}

const PromiseDetailsPage: React.FC = () => {
  const { promiseId } = useParams<{ promiseId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<EditablePromiseData | null>(null);
  const [newNote, setNewNote] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactionDrawerOpen, setTransactionDrawerOpen] = useState(false);
  const [logCallModalOpen, setLogCallModalOpen] = useState(false);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [reactivateCaseDialogOpen, setReactivateCaseDialogOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<UpdatePromiseToPayRequest | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [addTransactionModalOpen, setAddTransactionModalOpen] = useState(false);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  const pendingNewContactId = useRef<string | null>(null);

  // Transactions added via the modal but not yet saved — shown as pending rows
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);

  // Transaction pagination state
  const [txCurrentPage, setTxCurrentPage] = useState(1);
  const [txItemsPerPage, setTxItemsPerPage] = useState(100);
  const [transactionSearchQuery, setTransactionSearchQuery] = useState('');

  // Track which invoices are promised (all invoices are promised by default)
  const [promisedInvoiceIds, setPromisedInvoiceIds] = useState<Set<string>>(new Set());

  // State for tracking promised amounts for each invoice
  const [promisedAmounts, setPromisedAmounts] = useState<Map<string, number>>(new Map());

  const {
    activities,
    isLoading: activitiesLoading,
    isFetchingMore: activitiesFetchingMore,
    hasMore: activitiesHasMore,
    loadMore: activitiesLoadMore,
    refetch: refetchActivities,
  } = usePromiseRecentActivitiesGraphQL(promiseId);
  const { data: activeUsers = [] } = useCompanyUsersList();
  const { selectedCompany } = useCompany();
  const updatePromiseMutation = useUpdatePromiseToPay();
  const addNoteMutation = useAddPromiseNote();
  const queryClient = useQueryClient();

  const {
    columns: promiseDetailCols,
    savePreferences: savePromiseDetailPrefs,
    resetToDefaults: resetPromiseDetailPrefs,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.PromiseDetailsTransactions,
    DEFAULT_PROMISE_DETAIL_TX_COLUMNS,
  );
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    promiseDetailCols.forEach((col) => {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    });
    return vis;
  }, [promiseDetailCols]);

  // Sorting state for transactions table
  const [sortBy, setSortBy] = useState<string>('transactionDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch the current promise directly by ID (supports navigation from case details hyperlink)
  const { data: promise = null, isLoading: isPromiseLoading } = usePromiseById(promiseId);
  const { data: contacts = [] } = useCustomerContactsList(promise?.customerId ?? '0');

  // Fetch promise transactions via dedicated GraphQL endpoint (linked transactions only)
  const {
    transactions: promiseTransactions,
    pagination: txPagination,
    refetch: refetchTransactions,
  } = usePromiseTransactionsPaginatedGraphQL(promiseId, {
    promiseId,
    page: txCurrentPage,
    pageSize: txItemsPerPage,
    search: transactionSearchQuery,
    sortBy,
    sortOrder,
  });

  // Calculate total promised amount from checked transactions
  const calculatedPromisedAmount = useMemo(() => {
    let total = 0;
    promisedInvoiceIds.forEach((invoiceId) => {
      const amount = promisedAmounts.get(invoiceId);
      if (amount !== undefined) {
        total += amount;
      }
    });
    return roundCurrency(total);
  }, [promisedInvoiceIds, promisedAmounts]);

  // Initialize promised invoices when promise data loads (all invoices are promised by default)
  useEffect(() => {
    if (promise?.invoices) {
      setPromisedInvoiceIds(new Set(promise.invoices.map((inv) => inv.id)));
      // Initialize promised amounts to outstanding amounts by default
      const amounts = new Map<string, number>();
      promise.invoices.forEach((inv) => {
        amounts.set(inv.id, inv.balanceRemaining);
      });
      setPromisedAmounts(amounts);
    }
  }, [promise?.invoices]);

  useEffect(() => {
    if (pendingNewContactId.current && contacts.length > 0) {
      const newContact = contacts.find((c) => c.id === pendingNewContactId.current);
      if (newContact) {
        pendingNewContactId.current = null;
      }
    }
  }, [contacts]);

  // Build a map of transaction ID -> outstanding amount from the live transaction data
  // This ensures validation always compares against the real outstanding, not the promise balance
  // Include pending transactions so they are factored into validation checks
  const outstandingByTxId = useMemo(() => {
    const map = new Map<string, number>();
    promiseTransactions.forEach((tx) => {
      map.set(String(tx.id), tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0);
    });
    pendingTransactions.forEach((tx) => {
      if (!map.has(String(tx.id))) {
        map.set(String(tx.id), tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0);
      }
    });
    return map;
  }, [promiseTransactions, pendingTransactions]);

  // Track the total outstanding for ALL linked + pending transactions regardless of search filter.
  // When transactionSearchQuery is empty the server returns the full (unfiltered) set, so we
  // capture that total in a ref. When search is active the server returns a subset, but the
  // ref still holds the correct unfiltered total. Pending transaction outstanding is always
  // added on top because pending transactions are client-side only.
  const unfilteredOutstandingTotalRef = useRef<number>(0);

  useEffect(() => {
    if (!transactionSearchQuery) {
      const serverTotal = roundCurrency(
        promiseTransactions.reduce((sum, tx) => sum + (tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0), 0),
      );
      unfilteredOutstandingTotalRef.current = serverTotal;
    }
  }, [promiseTransactions, transactionSearchQuery]);

  const totalOutstandingAll = useMemo(() => {
    const pendingTotal = roundCurrency(
      pendingTransactions.reduce((sum, tx) => sum + (tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0), 0),
    );
    return roundCurrency(unfilteredOutstandingTotalRef.current + pendingTotal);
  }, [pendingTransactions, unfilteredOutstandingTotalRef.current]);

  // Stable set of ALL pending transaction IDs (for green row styling in table)
  const allPendingTransactionIds = useMemo(
    () => new Set(pendingTransactions.map((tx) => tx.id)),
    [pendingTransactions],
  );

  const filteredPendingTransactions = useMemo(() => {
    if (!isEditMode) return [];
    const serverIds = new Set((promiseTransactions as unknown as Transaction[]).map((tx) => tx.id));
    const dedupedPending = pendingTransactions.filter((tx) => !serverIds.has(tx.id));
    return transactionSearchQuery
      ? dedupedPending.filter((tx) => matchesTransactionSearch(tx, transactionSearchQuery))
      : dedupedPending;
  }, [promiseTransactions, pendingTransactions, isEditMode, transactionSearchQuery]);

  const visibleTransactions = useMemo(() => {
    const serverTxs = promiseTransactions as unknown as Transaction[];
    if (!isEditMode) return serverTxs;

    const serverTotal = txPagination.totalItems;
    const pageStart = (txCurrentPage - 1) * txItemsPerPage;
    const pageEnd = txCurrentPage * txItemsPerPage;
    const pendingStart = Math.max(0, pageStart - serverTotal);
    const pendingEnd = Math.max(0, pageEnd - serverTotal);
    const pendingSlice = filteredPendingTransactions.slice(pendingStart, pendingEnd);

    return [...serverTxs, ...pendingSlice];
  }, [
    promiseTransactions,
    filteredPendingTransactions,
    isEditMode,
    txPagination.totalItems,
    txCurrentPage,
    txItemsPerPage,
  ]);

  // Handler to update promised amount for an invoice
  const handlePromisedAmountChange = (invoiceId: string, value: number | null) => {
    const numValue = value ?? 0;

    // Use the transaction outstanding amount for validation cap
    const txOutstanding = outstandingByTxId.get(invoiceId);
    if (txOutstanding !== undefined) {
      const error = validatePromisedAmount(numValue, txOutstanding);
      if (error) {
        toast.error(error);
        return;
      }
    } else {
      const invoice = promise?.invoices.find((inv) => inv.id === invoiceId || String(inv.transactionId) === invoiceId);
      if (invoice) {
        const outstanding = invoice.outstandingAmount ?? invoice.balanceRemaining;
        const error = validatePromisedAmount(numValue, outstanding);
        if (error) {
          toast.error(error);
          return;
        }
      }
    }

    setPromisedAmounts((prev) => {
      const newMap = new Map(prev);
      newMap.set(invoiceId, numValue);
      return newMap;
    });
  };

  const {
    auditLogs,
    isLoading: auditLogsLoading,
    isFetchingMore: auditLogsFetchingMore,
    hasMore: auditLogsHasMore,
    loadMore: auditLogsLoadMore,
    refetch: refetchAuditLogs,
  } = usePromiseAuditLogsGraphQL(promise?.id, promise?.promisedAmount || 0);

  // Filter activities based on selected tags
  const filteredActivities =
    selectedFilters.length === 0 ? activities : activities.filter((activity) => selectedFilters.includes(activity.tag));

  // Initialize edit data when entering edit mode
  const handleEnterEditMode = () => {
    if (!promise) return;
    setEditedData({
      promiseDate: promise.promiseDate,
      promisedAmount: promise.promisedAmount,
      status: promise.status,
      confirmationMethod: promise.confirmationMethod,
      assignedUserId: promise.assignedUserId,
      contactId: promise.contactId || '',
      notes: promise.notes,
    });
    // Select all transactions by default and initialize promised amounts from transaction data
    setPromisedInvoiceIds(new Set(promiseTransactions.map((tx) => String(tx.id))));
    const amounts = new Map<string, number>();
    promiseTransactions.forEach((tx) => {
      amounts.set(String(tx.id), tx.promisedAmount);
    });
    setPromisedAmounts(amounts);
    setIsEditMode(true);
  };

  // Cancel edit mode — clear all edit state and reload from database
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
    setPendingTransactions([]);
    // Clear search so view mode shows the full unfiltered list
    setTransactionSearchQuery('');
    // Restore original invoice-based state
    if (promise?.invoices) {
      setPromisedInvoiceIds(new Set(promise.invoices.map((inv) => inv.id)));
      const amounts = new Map<string, number>();
      promise.invoices.forEach((inv) => {
        amounts.set(inv.id, inv.balanceRemaining);
      });
      setPromisedAmounts(amounts);
    }
    // Refetch transactions from database to ensure view mode shows clean data
    refetchTransactions();
  };

  // Execute the update mutation with the given request data
  const executeSave = (request: UpdatePromiseToPayRequest) => {
    updatePromiseMutation.mutate(request, {
      onSuccess: () => {
        toast.success('Promise details updated successfully');
        setIsEditMode(false);
        setEditedData(null);
        setPendingTransactions([]);
        // Reset local state so view mode doesn't show stale edit-mode data
        setPromisedInvoiceIds(new Set());
        setPromisedAmounts(new Map());
        // Invalidate and refetch all relevant queries to get fresh data from the database
        queryClient.invalidateQueries({
          queryKey: ['promise-to-pay', promiseId],
        });
        queryClient.invalidateQueries({ queryKey: ['promises-to-pay'] });
        queryClient.invalidateQueries({
          queryKey: ['promise-transactions-graphql-list'],
        });
        queryClient.invalidateQueries({
          queryKey: ['promise-transactions-graphql-count'],
        });
        refetchTransactions();
        refetchActivities();
        refetchAuditLogs();
      },
      onError: (error) => {
        toast.error(`Failed to update promise: ${error.message}`);
      },
    });
  };

  // Save changes
  const handleSaveChanges = () => {
    if (!editedData || !promise) return;

    const validationError = validatePromiseChanges(
      editedData,
      promise,
      calculatedPromisedAmount,
      promisedInvoiceIds,
      promisedAmounts,
      outstandingByTxId,
    );
    if (validationError) {
      toast.error(validationError.message);
      return;
    }

    // Build linked invoices from selected transactions and their promised amounts
    const linkedInvoices = Array.from(promisedInvoiceIds)
      .map((txId) => ({
        transactionId: parseInt(txId, 10),
        promisedAmount: promisedAmounts.get(txId) ?? 0,
      }))
      .filter((item) => item.transactionId > 0);

    const saveData: UpdatePromiseToPayRequest = {
      promiseId: promise.id,
      promiseDate: format(editedData.promiseDate, 'yyyy-MM-dd'),
      promisedAmount: calculatedPromisedAmount,
      confirmationMethodId: mapConfirmationMethodToId(editedData.confirmationMethod),
      assignedUserId: editedData.assignedUserId,
      contactId: editedData.contactId ? parseInt(editedData.contactId, 10) : undefined,
      notes: editedData.notes,
      status: editedData.status,
      linkedInvoices,
    };

    const isClosing = editedData.status?.toLowerCase() === 'closed' && promise.status?.toLowerCase() !== 'closed';
    if (isClosing && promise.caseNumber) {
      setPendingSaveData(saveData);
      setReactivateCaseDialogOpen(true);
      return;
    }

    executeSave(saveData);
  };

  const handleReactivateCaseConfirm = () => {
    if (!pendingSaveData) return;
    executeSave({ ...pendingSaveData, reactivateCase: true });
    setPendingSaveData(null);
    setReactivateCaseDialogOpen(false);
  };

  const handleReactivateCaseDecline = () => {
    if (!pendingSaveData) return;
    executeSave({ ...pendingSaveData, reactivateCase: false });
    setPendingSaveData(null);
    setReactivateCaseDialogOpen(false);
  };

  // Add new note
  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    if (!promiseId) return;

    addNoteMutation.mutate(
      { promiseId, notes: newNote.trim() },
      {
        onSuccess: () => {
          toast.success('Note added successfully');
          setNewNote('');
          refetchActivities();
          refetchAuditLogs();
        },
        onError: (error) => {
          toast.error(`Failed to add note: ${error.message}`);
        },
      },
    );
  };

  // Handle transaction click - open detail sheet
  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDrawerOpen(true);
  };

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    savePromiseDetailPrefs(columns);
  };

  // Add transactions from modal — store as pending and track their IDs/amounts
  const handleAddTransactionsToPromise = useCallback((transactions: Transaction[]) => {
    // Append new pending transactions (avoid duplicates)
    setPendingTransactions((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const newTxs = transactions.filter((tx) => !existingIds.has(tx.id));
      return [...prev, ...newTxs];
    });
    setPromisedInvoiceIds((prev) => {
      const newSet = new Set(prev);
      for (const tx of transactions) {
        newSet.add(String(tx.id));
      }
      return newSet;
    });
    setPromisedAmounts((prev) => {
      const newMap = new Map(prev);
      for (const tx of transactions) {
        const txId = String(tx.id);
        if (!newMap.has(txId)) {
          newMap.set(txId, tx.displayOutstandingAmount ?? tx.outstandingAmount ?? 0);
        }
      }
      return newMap;
    });
    setAddTransactionModalOpen(false);
  }, []);

  const handleSortChange = useCallback((newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);

  // Server-side XLSX export — build columns and filter
  const getExportFilter = useCallback((): Record<string, unknown> | undefined => {
    return buildPromiseTransactionExportFilter(promiseId);
  }, [promiseId]);

  const getExportSummary = useCallback((): string | undefined => {
    return transactionSearchQuery || undefined;
  }, [transactionSearchQuery]);

  const getVisibleExportColumns = useCallback((): ColumnInput[] => {
    return promiseDetailCols
      .filter((col) => col.visible && col.id in PROMISE_TRANSACTION_EXPORT_COLUMN_MAP)
      .map((col) => ({
        name: PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }));
  }, [promiseDetailCols]);

  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_PROMISE_DETAIL_TX_COLUMNS.filter((col) => col.id in PROMISE_TRANSACTION_EXPORT_COLUMN_MAP).map(
      (col) => ({
        name: PROMISE_TRANSACTION_EXPORT_COLUMN_MAP[col.id],
        displayName: col.label,
      }),
    );
  }, []);

  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: Forms.PromiseTransactions,
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `promise-transactions-${promise?.customerName || 'promise'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: Forms.PromiseTransactions,
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    getSummary: getExportSummary,
    fileName: `promise-transactions-all-columns-${promise?.customerName || 'promise'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const handleExportCurrentView = useCallback(() => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportCurrentView]);

  const handleExportAllColumns = useCallback(() => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  }, [triggerExportAllColumns]);

  // Clear filters
  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const activeFilterCount = selectedFilters.length;

  if (isPromiseLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!promise) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Promise Not Found</h2>
          <p className="text-muted-foreground mb-4">The promise you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/collections/promises')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Promises
          </Button>
        </div>
      </div>
    );
  }

  const displayData = isEditMode && editedData ? editedData : promise;

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar */}
      <div
        className={cn('relative transition-all duration-300 ease-in-out shrink-0', isSidebarCollapsed ? 'w-0' : 'w-80')}
      >
        <PromiseDetailsSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Vertical Tab/Handle - Only visible when sidebar is collapsed */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Promises"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="writing-mode-vertical text-xs font-medium tracking-wider">PROMISES</div>
              </div>
            </div>
          </button>
        )}

        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold flex items-center gap-2">
                      <HandCoins className="h-6 w-6 text-primary" />
                      Promise Details
                    </h1>
                    <p className="text-muted-foreground">Payment commitment from {promise.customerName}</p>
                    {/* Promise ID Badge */}
                    <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                      <span className="text-sm text-muted-foreground">Promise ID:</span>
                      <span className="text-sm font-semibold text-primary">{promise.id}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/collections/promises')}>
                    Back to Promises
                  </Button>
                  {isEditMode ? (
                    <>
                      <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveChanges}>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  <HelpSupportButton screenId="promise-details" size="sm" />
                </div>
              </div>

              {/* Edit Mode Indicator */}
              {isEditMode && (
                <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Edit className="h-4 w-4" />
                  <span className="font-medium">Edit Mode Active</span>
                  <span className="text-muted-foreground">- Make changes and click Save to update</span>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-4 border-b shrink-0">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="activity" className="flex items-center gap-2">
                  Activity
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {activities.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="audit">Audit Log</TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Overview Tab */}
              <OverviewTabContent
                promise={promise}
                isEditMode={isEditMode}
                editedData={editedData}
                displayData={displayData}
                navigate={navigate}
                setSendEmailModalOpen={setSendEmailModalOpen}
                setLogCallModalOpen={setLogCallModalOpen}
                setActiveTab={setActiveTab}
                setEditedData={setEditedData}
                columnVisibility={columnVisibility}
                promiseTransactions={promiseTransactions}
                promisedInvoiceIds={promisedInvoiceIds}
                promisedAmounts={promisedAmounts}
                calculatedPromisedAmount={calculatedPromisedAmount}
                handlePromisedAmountChange={handlePromisedAmountChange}
                handleTransactionClick={handleTransactionClick}
                setPromisedInvoiceIds={setPromisedInvoiceIds}
                handleExportCurrentView={handleExportCurrentView}
                handleExportAllColumns={handleExportAllColumns}
                handleColumnConfigApply={handleColumnConfigApply}
                resetPromiseDetailPrefs={resetPromiseDetailPrefs}
                promiseDetailCols={promiseDetailCols}
                activeUsers={activeUsers}
                txPagination={txPagination}
                txCurrentPage={txCurrentPage}
                txItemsPerPage={txItemsPerPage}
                setTxCurrentPage={setTxCurrentPage}
                setTxItemsPerPage={setTxItemsPerPage}
                onSortChange={handleSortChange}
                transactionSearchQuery={transactionSearchQuery}
                setTransactionSearchQuery={setTransactionSearchQuery}
                onOpenAddTransactionModal={() => setAddTransactionModalOpen(true)}
                pendingTransactions={pendingTransactions}
                allPendingTransactionIds={allPendingTransactionIds}
                visibleTransactions={visibleTransactions}
                totalOutstandingAll={totalOutstandingAll}
                filteredPendingCount={filteredPendingTransactions.length}
                contacts={contacts}
                setAddContactDialogOpen={setAddContactDialogOpen}
              />

              {/* Activity Tab - Consolidated view of Actions and Notes */}
              <ActivityTabContent
                filteredActivities={filteredActivities}
                activitiesLoading={activitiesLoading}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                clearFilters={clearFilters}
                activeFilterCount={activeFilterCount}
                newNote={newNote}
                setNewNote={setNewNote}
                handleAddNote={handleAddNote}
                hasMore={activitiesHasMore}
                isFetchingMore={activitiesFetchingMore}
                onLoadMore={activitiesLoadMore}
              />

              {/* Audit Log Tab */}
              <AuditLogTabContent
                auditLogs={auditLogs}
                auditLogsLoading={auditLogsLoading}
                hasMore={auditLogsHasMore}
                isFetchingMore={auditLogsFetchingMore}
                onLoadMore={auditLogsLoadMore}
                currencyCode={promise?.currencyCode || selectedCompany?.currency || 'GBP'}
              />
            </div>
          </Tabs>

          {/* Transaction Details Drawer */}
          <TransactionDetailSheet
            open={transactionDrawerOpen}
            onOpenChange={setTransactionDrawerOpen}
            transaction={selectedTransaction}
          />

          {/* Log Call Modal */}
          <LogCallModal
            open={logCallModalOpen}
            onOpenChange={setLogCallModalOpen}
            customerName={promise?.customerName || ''}
            customerPhone={promise?.customerPhone}
            promiseId={promise?.id}
            onSuccess={() => {
              refetchActivities();
              refetchAuditLogs();
            }}
          />

          {/* Send Email Modal */}
          <PromiseSendEmailModal
            open={sendEmailModalOpen}
            onOpenChange={setSendEmailModalOpen}
            promiseId={promise?.id || ''}
            customerId={promise?.customerId || ''}
            customerName={promise?.customerName || ''}
            accountNumber={promise?.customerAccountNumber || ''}
            onSuccess={() => {
              refetchActivities();
              refetchAuditLogs();
            }}
          />

          {/* Add Transaction Modal */}
          {promise && (
            <SelectTransactionsModal
              open={addTransactionModalOpen}
              onOpenChange={setAddTransactionModalOpen}
              customerId={Number(promise.customerId)}
              context="promise"
              promiseId={Number(promiseId)}
              onAddTransactions={handleAddTransactionsToPromise}
              currency={promise.currencyCode || undefined}
            />
          )}

          <AddContactDialog
            customerId={promise?.customerId ?? ''}
            defaultCompany={promise?.customerName || promise?.customerAccountNumber || ''}
            open={addContactDialogOpen}
            onOpenChange={setAddContactDialogOpen}
            onContactCreated={(newId) => {
              pendingNewContactId.current = newId;
              if (editedData) {
                setEditedData({ ...editedData, contactId: newId });
              }
            }}
          />

          {/* Reactivate Case Dialog */}
          <AlertDialog open={reactivateCaseDialogOpen} onOpenChange={setReactivateCaseDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reactivate Case</AlertDialogTitle>
                <AlertDialogDescription>
                  Do you want to reactivate the case: {promise?.caseNumber}?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={handleReactivateCaseDecline}>No</AlertDialogCancel>
                <AlertDialogAction onClick={handleReactivateCaseConfirm}>Yes</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

function PromiseDetailsPageWrapper() {
  return (
    <ExportProvider>
      <PromiseDetailsPage />
    </ExportProvider>
  );
}

export default PromiseDetailsPageWrapper;
